import * as React from 'react';
import { useEffect } from 'react';
import Box from '@mui/material/Box';
import Slider from '@mui/material/Slider';
import { styled, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { isEmpty } from 'lodash';

function valueText(value: number, units?: string) {
    return units ? `${value}${units}` : `${value}`;
}

interface InputSliderRangeProps {
    name: string;
    marks?: { value: number; label: string }[];
    min?: number;
    max?: number;
    units?: string;
    defaultMin?: number;
    defaultMax?: number;
    minLabel?: string;
    maxLabel?: string;
    minDistance?: number;
}

const SliderRange = styled(Slider)(({ theme }) => ({
    marginTop: '20px',
    '& .MuiSlider-valueLabelOpen': {
        borderRadius: '12px',
        background: theme.palette.primaryPurple.main,
        fontSize: '12px',
        color: theme.palette.neutralWhite.main,
        fontWeight: 600,
        lineHeight: 'normal',
    },

    '& .MuiSlider-valueLabel': {
        whiteSpace: 'nowrap',
        padding: '4px 8px',
        minWidth: 'fit-content',
    },

    '& .MuiSlider-rail': {
        background: '#F7F6FF',
        height: '6px',
    },
}));

export default function InputSliderRange({
    name,
    marks,
    min,
    max,
    defaultMin,
    defaultMax,
    units,
    minLabel,
    maxLabel,
    minDistance,
}: InputSliderRangeProps) {
    const { control, setValue: setFormValue } = useFormContext();
    const [value, setValue] = React.useState<number[]>([defaultMin, defaultMax]);

    const handleChange1 = (event: Event, newValue: number[], activeThumb: number) => {
        if (activeThumb === 0) {
            setValue([Math.min(newValue[0], value[1] - minDistance), value[1]]);
        } else {
            setValue([value[0], Math.max(newValue[1], value[0] + minDistance)]);
        }
    };

    useEffect(() => {
        if (!isEmpty(value[0])) {
            setFormValue(`${name}.min`, value[0]);
        }
        if (!isEmpty(value[1])) {
            setFormValue(`${name}.max`, value[1]);
        }
    }, [name, setFormValue, value]);

    return (
        <Box sx={{ padding: '10px' }}>
            <Controller
                name={`${name}`}
                control={control}
                render={({ field }) => (
                    <SliderRange
                        getAriaLabel={() => 'Minimum distance'}
                        value={value}
                        onChange={handleChange1}
                        valueLabelFormat={val => valueText(val, units)}
                        disableSwap
                        marks={marks}
                        valueLabelDisplay="on"
                        min={min}
                        max={max}
                    />
                )}
            />
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: '16px',
                    fontWeight: 600,
                    color: theme => theme.palette.neutralDarkGray.main,
                    fontFamily: theme => theme.typography.fontFamily,
                }}>
                <Typography variant="body2" sx={{ cursor: 'pointer' }}>
                    {minLabel}
                </Typography>
                <Typography variant="body2" sx={{ cursor: 'pointer' }}>
                    {maxLabel}
                </Typography>
            </Box>
        </Box>
    );
}
